/**
 * schoolSubjectAdapter — maps API / static-data school subjects → `UISubject`.
 *
 * Lives in `ports/primary/adapters/` (API-aware). The DS never sees hex colors:
 * slug/id → `SemanticColorKey` (`subject-maths`, …) registered on the theme.
 */
import { type SemanticColorKey } from "../../../../../design-system/theme/tokens/colors"
import { type UISubject } from "../../../ui/types"

/** Minimal API / static-data subject shape we care about. */
export type ApiSchoolSubject = {
    id?: string | number
    name?: string
    slug?: string
    color?: string
    icon?: string
}

const SUBJECT_TOKEN_BY_SLUG: Record<string, SemanticColorKey> = {
    maths: "subject-maths",
    physics: "subject-physics",
    biology: "subject-biology",
    litterature: "subject-french",
    french: "subject-french",
    history_geography: "subject-history",
    english: "subject-english",
    spanish: "subject-spanish",
    german: "subject-german",
    portuguese: "subject-portuguese",
    philosophy: "subject-philosophy",
    economics: "subject-economics",
    emc: "subject-emc",
    italian: "subject-italian",
}

const SUBJECT_TOKEN_BY_HEX: Record<string, SemanticColorKey> = {
    "#00adea": "subject-maths",
    "#000080": "subject-physics",
    "#ff8d00": "subject-biology",
    "#ff0000": "subject-french",
    "#8b0000": "subject-history",
    "#008000": "subject-english",
    "#ffd700": "subject-spanish",
    "#895d33": "subject-german",
    "#cc0842": "subject-portuguese",
    "#800080": "subject-philosophy",
    "#ffa500": "subject-economics",
    "#ff00ff": "subject-emc",
    "#cd212a": "subject-italian",
}

export function subjectColorToken(api: ApiSchoolSubject): SemanticColorKey {
    const slug = api.slug?.toLowerCase()
    if (slug && SUBJECT_TOKEN_BY_SLUG[slug]) return SUBJECT_TOKEN_BY_SLUG[slug]

    const hex = api.color?.trim().toLowerCase()
    if (hex && SUBJECT_TOKEN_BY_HEX[hex]) return SUBJECT_TOKEN_BY_HEX[hex]

    return "subject-default"
}

export function toUISubject(api: ApiSchoolSubject | null | undefined): UISubject {
    if (!api) {
        return { id: 0, name: "Matière inconnue", color: "subject-default" }
    }

    return {
        id: api.id ?? 0,
        name: api.name ?? "Matière inconnue",
        color: subjectColorToken(api),
        iconUrl: api.icon,
    }
}

export function indexSubjectsById(subjects: ApiSchoolSubject[] | null | undefined): Record<string, ApiSchoolSubject> {
    const out: Record<string, ApiSchoolSubject> = {}
    for (const s of subjects ?? []) {
        if (s?.id != null) out[String(s.id)] = s
    }
    return out
}
